import { useGlobalPlayer } from "@bandwith/recordings";
import {
  ChordProHighlight,
  ChordProRenderer,
  createInitialState,
  type FretboardState,
  formatChordName,
  InteractiveFretboard,
  identifyChord,
  playChordFromDefinition,
  playStringNote,
} from "@bandwith/songs";
import { Mic, Play, Quote, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AuthLinkButton } from "../components/AuthLinkButton";
import { RecordingActions } from "../components/RecordingActions";
import {
  type MediaItem,
  SortableMediaList,
} from "../components/SortableMediaList";

/** Simple lyrics anchor chip for demo - shows proper styling */
function LyricsAnchorChip({
  anchorText,
  onClick,
}: {
  anchorText: string;
  onClick?: (text: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(anchorText)}
      className="inline-flex items-center gap-1 rounded-full bg-amber-warm/10 px-2 py-0.5 align-middle text-xs font-medium text-text-muted transition-colors hover:bg-amber-warm/20"
      title={`Jump to "${anchorText}" in lyrics`}
    >
      <Quote className="h-3 w-3" aria-hidden="true" />
      <span className="max-w-32 truncate">"{anchorText}"</span>
    </button>
  );
}

/** Simple recording link chip for demo - shows proper styling with play button */
function RecordingLinkChip({
  filename,
  timestamp,
  onClick,
}: {
  filename: string;
  timestamp?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full bg-amber/10 px-2 py-0.5 align-middle text-xs font-medium text-amber transition-colors hover:bg-amber/20"
    >
      <Play className="h-3 w-3" aria-hidden="true" />
      <span className="font-mono">{filename}</span>
      {timestamp && <span className="text-amber/70">@ {timestamp}</span>}
    </button>
  );
}

// ChordPro content with two chord diagrams
const demoChordPro = `{chord: G}
{define: Gchill base-fret 1 frets 3 x 0 0 1 3 fingers 2 x - - 1 3}

{start_of_verse}
[G]Strum along, [D]tap a chord
[Em]Hear the [C]sound
{end_of_verse}

{soc}
[G]This is [D]Bandwith
[C]So bandmates: [Gchill]gather round
{eoc}`;

// Demo feedback
const DEMO_FEEDBACK = {
  id: "feedback-1",
  body: '{@"gather round"} Love this hook! Maybe try a Cm instead of C here?',
  author: { name: "Lyra" },
};

// Convert to InlineFeedback shape for ChordProRenderer
const INLINE_FEEDBACK = [
  {
    id: DEMO_FEEDBACK.id,
    body: DEMO_FEEDBACK.body,
    author: {
      name: DEMO_FEEDBACK.author.name,
      image: null,
    },
  },
];

type ViewMode = "output" | "input";

function DemoSongSection() {
  const [viewMode, setViewMode] = useState<ViewMode>("output");

  const handleInlineCommentClick = useCallback((feedbackId: string) => {
    const element = document.getElementById(`feedback-${feedbackId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("animate-highlight-new");
      setTimeout(() => {
        element.classList.remove("animate-highlight-new");
      }, 2000);
    }
  }, []);

  return (
    <section className="space-y-6">
      <div>
        <h2 className="mb-2 text-xl font-semibold text-text-primary">
          Lyrics, Chords, and Feedback
        </h2>
        <p className="text-sm text-text-muted">
          Write lyrics with{" "}
          <a
            href="https://www.chordpro.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber hover:underline"
          >
            ChordPro
          </a>{" "}
          notation. Tap any chord to hear it, or tap a diagram to play the full
          voicing. Leave feedback anchored to specific lyrics—comments appear
          inline and in the full thread below.
        </p>
      </div>

      {/* View mode toggle */}
      <fieldset className="flex items-center justify-center gap-1">
        <legend className="sr-only">View mode</legend>
        <span className="mr-2 text-sm text-text-muted">Show:</span>
        <div className="inline-flex rounded-md border border-border-subtle">
          <label
            className={`cursor-pointer rounded-l-md px-3 py-1.5 text-sm transition-colors ${
              viewMode === "output"
                ? "bg-amber/10 text-amber"
                : "text-text-muted hover:bg-bg-tertiary hover:text-text-secondary"
            }`}
          >
            <input
              type="radio"
              name="demo-view-mode"
              value="output"
              checked={viewMode === "output"}
              onChange={() => setViewMode("output")}
              className="sr-only"
            />
            Song View
          </label>
          <label
            className={`cursor-pointer rounded-r-md border-l border-border-subtle px-3 py-1.5 text-sm transition-colors ${
              viewMode === "input"
                ? "bg-amber/10 text-amber"
                : "text-text-muted hover:bg-bg-tertiary hover:text-text-secondary"
            }`}
          >
            <input
              type="radio"
              name="demo-view-mode"
              value="input"
              checked={viewMode === "input"}
              onChange={() => setViewMode("input")}
              className="sr-only"
            />
            ChordPro
          </label>
        </div>
      </fieldset>

      {/* Song content */}
      {viewMode === "output" ? (
        <div className="rounded-lg border border-border-subtle bg-bg-secondary p-4">
          <ChordProRenderer
            content={demoChordPro}
            showDiagrams={true}
            inlineFeedback={INLINE_FEEDBACK}
            showInlineComments={true}
            onInlineCommentClick={handleInlineCommentClick}
          />
        </div>
      ) : (
        <ChordProHighlight>{demoChordPro}</ChordProHighlight>
      )}

      {/* Feedback section */}
      <div className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-text-muted">
          Feedback
        </h3>

        <div
          id={`feedback-${DEMO_FEEDBACK.id}`}
          className="rounded-lg border border-border-subtle bg-bg-secondary p-4"
        >
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-tertiary text-xs text-text-muted">
              {DEMO_FEEDBACK.author.name[0]}
            </div>
            <span className="text-sm font-medium text-text-primary">
              {DEMO_FEEDBACK.author.name}
            </span>
            <span className="text-xs text-text-muted">just now</span>
          </div>
          <p className="text-text-secondary">
            <LyricsAnchorChip anchorText="gather round" /> Love this hook! Maybe
            try a Cm instead of C here?
          </p>
        </div>
      </div>
    </section>
  );
}

// Demo recording for the media list
const DEMO_RECORDING: MediaItem = {
  id: "demo-recording-1",
  title: "keeping-new-york-in-the-divorce.m4a",
  titleType: "filename",
  audio: {
    recordingId: "demo-recording-1",
    filename: "keeping-new-york-in-the-divorce.m4a",
    signedUrl: "/demo-recording.m4a",
    songId: "demo",
    songTitle: "Keeping New York in the Divorce",
    authorName: null,
    durationSeconds: 45,
  },
};

function DemoRecordingsSection() {
  const { play, seekTo, tracks, currentTrackIndex } = useGlobalPlayer((s) => ({
    play: s.play,
    seekTo: s.seekTo,
    tracks: s.tracks,
    currentTrackIndex: s.currentTrackIndex,
  }));

  // Handler for timestamp chip clicks - play the demo and seek to 14 seconds
  const handleTimestampClick = useCallback(() => {
    const demoTrack = {
      id: DEMO_RECORDING.audio!.recordingId,
      filename: DEMO_RECORDING.audio!.filename,
      signedAudioUrl: DEMO_RECORDING.audio!.signedUrl,
      songId: DEMO_RECORDING.audio!.songId,
      songTitle: DEMO_RECORDING.audio!.songTitle,
      authorName: DEMO_RECORDING.audio!.authorName,
    };

    // Check if this track is already playing
    const currentTrack =
      currentTrackIndex !== null ? tracks[currentTrackIndex] : null;
    const isCurrentTrack = currentTrack?.id === demoTrack.id;

    if (isCurrentTrack) {
      // Just seek if already playing
      seekTo(14);
    } else {
      // Start playing then seek
      play([demoTrack], 0, false);
      // Small delay to let track load before seeking
      setTimeout(() => seekTo(14), 100);
    }
  }, [play, seekTo, tracks, currentTrackIndex]);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="mb-2 text-xl font-semibold text-text-primary">
          Your Tracks: Tracked
        </h2>
        <p className="text-sm text-text-muted">
          Record in the browser, upload files, or even send an email. Everyone
          can listen, compare takes, and track how songs evolve.
        </p>
      </div>

      {/* Recordings list with one demo entry */}
      <SortableMediaList
        items={[DEMO_RECORDING]}
        isEditing={false}
        testIdPrefix="demo-recordings"
      />

      {/* Record/Upload buttons */}
      <RecordingActions variant="compact" />

      {/* Email inset */}
      <div className="rounded-lg border border-border-subtle bg-bg-secondary p-4 font-mono text-sm">
        <div className="mb-2">
          <span className="text-text-muted">To:</span>{" "}
          <a
            href="mailto:demos@bandwith.rocks"
            className="text-text-primary hover:text-amber hover:underline"
          >
            demos@bandwith.rocks
          </a>
        </div>
        <div className="mb-2">
          <span className="text-text-muted">Subject:</span>{" "}
          <span className="text-text-primary">New Song Idea</span>
        </div>
        <div className="mb-2">
          <span className="text-text-muted">Attachments:</span>{" "}
          <span className="text-text-primary">demo.mp3</span>
        </div>
        <div className="mt-2 border-t border-border-subtle pt-2 text-text-muted">
          [G]Here's a verse I [C]just wrote...
        </div>
      </div>

      {/* Timestamp feedback example */}
      <div className="space-y-3 pt-2">
        <p className="text-sm text-text-muted">
          Refer to specific moments in your feedback. Tap a timestamp to jump to
          it in the recording.
        </p>
        <div className="rounded-lg border border-border-subtle bg-bg-secondary p-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-tertiary text-xs text-text-muted">
              M
            </div>
            <span className="text-sm font-medium text-text-primary">Mike</span>
            <span className="text-xs text-text-muted">just now</span>
          </div>
          <p className="text-text-secondary">
            <RecordingLinkChip
              filename="keeping-new-york-in-the-divorce.m4a"
              timestamp="0:14"
              onClick={handleTimestampClick}
            />{" "}
            Could use some harmonies on this part.
          </p>
        </div>
      </div>
    </section>
  );
}

// Start with Am7 shape to show something interesting
const AM7_STATE: FretboardState = [
  { type: "muted" },
  { type: "open" },
  { type: "fretted", fret: 2 },
  { type: "open" },
  { type: "fretted", fret: 1 },
  { type: "open" },
];

function ChordFinderDemo() {
  const [fretboardState, setFretboardState] =
    useState<FretboardState>(AM7_STATE);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const result = identifyChord(fretboardState);
  const rawName = result.chordNames[0];

  let displayName: string;
  if (rawName) displayName = formatChordName(rawName);
  else if (result.notes.length > 0) displayName = "Unknown";
  else displayName = "\u2014";

  const handleFretboardChange = useCallback((newState: FretboardState) => {
    setFretboardState(newState);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const tempResult = identifyChord(newState);
      if (tempResult.notes.length > 0) {
        playChordFromDefinition({
          name: "preview",
          baseFret: 1,
          frets: tempResult.frets.map((f) => {
            if (f === "x" || f === 0) return f;
            return f + tempResult.baseFret - 1;
          }),
        });
      }
    }, 100);
  }, []);

  const handleStrum = useCallback(
    (stringIndices: number[]) => {
      for (const stringIndex of stringIndices) {
        const fret = result.frets[stringIndex];
        if (fret === undefined) continue;
        const absoluteFret =
          fret === "x" || fret === 0 ? fret : fret + result.baseFret - 1;
        playStringNote(stringIndex, absoluteFret, {
          duration: 1.5,
          volume: 0.25,
        });
      }
    },
    [result],
  );

  const handleClear = useCallback(() => {
    setFretboardState(createInitialState());
  }, []);

  const handlePlay = useCallback(() => {
    if (result.notes.length === 0) return;
    playChordFromDefinition({
      name: displayName,
      baseFret: 1,
      frets: result.frets.map((f) => {
        if (f === "x" || f === 0) return f;
        return f + result.baseFret - 1;
      }),
    });
  }, [result, displayName]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const hasNotes = result.notes.length > 0;

  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle bg-bg-secondary">
      <InteractiveFretboard
        state={fretboardState}
        onChange={handleFretboardChange}
        fingering={result.fingering}
        onStrum={handleStrum}
      />
      <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="p-1.5 text-text-muted transition-colors hover:text-text-primary"
            aria-label="Clear"
            title="Clear"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handlePlay}
            disabled={!hasNotes}
            className="p-1.5 text-text-muted transition-colors hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Play"
            title="Play"
          >
            <Play className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-muted">Detected:</span>
          <button
            type="button"
            onClick={handlePlay}
            disabled={!hasNotes}
            className="cursor-pointer font-mono font-medium text-amber hover:underline disabled:cursor-default disabled:no-underline"
          >
            {displayName}
          </button>
        </div>
      </div>
    </div>
  );
}

function AboutPageCTA() {
  return (
    <section className="space-y-3 pt-4 text-center">
      <p className="text-sm text-text-muted">1, 2, 3, 4!</p>
      <AuthLinkButton to="/songs/new?action=record">
        <Mic className="h-4 w-4" />
        Record
      </AuthLinkButton>
    </section>
  );
}

export function About() {
  return (
    <div className="space-y-12 pb-24">
      {/* Hero */}
      <section className="text-center">
        <h1 className="mb-4 text-3xl font-bold text-text-primary">
          Welcome to{" "}
          <span className="font-display tracking-tight text-amber">
            Bandwith
          </span>
        </h1>
        <p className="mx-auto max-w-lg text-lg text-text-muted">
          Organize demos, lyrics, and chords. Keep your band in sync—no click
          track required.
        </p>
      </section>

      {/* Unified Song Demo: Lyrics + Chords + Feedback */}
      <DemoSongSection />

      {/* Recordings Demo */}
      <DemoRecordingsSection />

      {/* Setlists Feature */}
      <section className="space-y-4">
        <div>
          <h2 className="mb-2 text-xl font-semibold text-text-primary">
            Setlists
          </h2>
          <p className="text-sm text-text-muted">
            Organize songs into setlists for rehearsals or gigs. Add notes for
            what to focus on, then hit Play All to run through the set.
          </p>
        </div>
      </section>

      {/* Chord Finder Feature */}
      <section className="space-y-4">
        <div>
          <h2 className="mb-2 text-xl font-semibold text-text-primary">
            Strike a Chord
          </h2>
          <p className="text-sm text-text-muted">
            Tap frets to identify a chord shape, or look up any chord from the
            built-in library. Tap any diagram to hear it.
          </p>
        </div>

        <ChordFinderDemo />
      </section>

      {/* CTA */}
      <AboutPageCTA />
    </div>
  );
}
